/**
 * Composant pour afficher l'historique des mouvements de stock d'un produit
 * Design System Volvo - Teal accent
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Package, Plus, Minus, RotateCcw, ArrowRightLeft, Undo2, Archive, ChevronLeft, ChevronRight, History } from 'lucide-react';
import type { Product } from '../../../types/models';
import { useStockMovements, type StockMovement, type StockMovementType } from '../hooks/useStockMovements';

interface StockMovementHistoryProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const movementTypeConfig: Record<StockMovementType, { labelKey: string; icon: React.ElementType; color: string; bgColor: string }> = {
  IN: { labelKey: 'products.stock.movementTypes.IN', icon: Plus, color: 'text-green-600', bgColor: 'bg-green-50' },
  OUT: { labelKey: 'products.stock.movementTypes.OUT', icon: Minus, color: 'text-red-600', bgColor: 'bg-red-50' },
  ADJUSTMENT: { labelKey: 'products.stock.movementTypes.ADJUSTMENT', icon: RotateCcw, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  RETURN: { labelKey: 'products.stock.movementTypes.RETURN', icon: Undo2, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  TRANSFER: { labelKey: 'products.stock.movementTypes.TRANSFER', icon: ArrowRightLeft, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  INITIAL: { labelKey: 'products.stock.movementTypes.INITIAL', icon: Archive, color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

export const StockMovementHistory: React.FC<StockMovementHistoryProps> = ({
  product,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  // UI state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // TanStack Query hook - only enabled when modal is open
  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useStockMovements(product.id, currentPage, limit, { enabled: isOpen });

  // Extract data from query
  const movements = data?.movements ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;
  const error = queryError ? (queryError instanceof Error ? queryError.message : t('products.stock.loadError')) : null;

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatUser = (user?: StockMovement['createdBy']) => {
    if (!user) return t('common.system');
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-400/20 w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in-up border border-white/50">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/30 bg-gradient-to-r from-teal-50/80 to-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              <History className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('products.stock.historyTitle')}</h2>
              <p className="text-sm text-gray-500 truncate max-w-[300px]">{product.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Stock actuel */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mb-6">
            <span className="text-sm font-medium text-gray-600">{t('products.stock.currentStock')}</span>
            <span className="text-2xl font-bold text-gray-900">
              {product.stockQuantity ?? 0} <span className="text-sm font-normal text-gray-500">{product.unit || t('products.stock.units')}</span>
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-600 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">{t('common.loading')}</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && movements.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('products.stock.noMovements')}</h3>
              <p className="text-sm text-gray-500">
                {t('products.stock.noMovementsDesc')}
              </p>
            </div>
          )}

          {/* Movements List */}
          {!loading && !error && movements.length > 0 && (
            <div className="space-y-3">
              {movements.map((movement) => {
                const config = movementTypeConfig[movement.type];
                const Icon = config.icon;
                const quantityDisplay = movement.type === 'OUT' ? `-${movement.quantity}` : `+${movement.quantity}`;

                return (
                  <div
                    key={movement.id}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                  >
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${config.bgColor}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${config.color}`}>
                          {t(config.labelKey)}
                        </span>
                        <span className={`text-lg font-bold ${movement.type === 'OUT' ? 'text-red-600' : 'text-green-600'}`}>
                          {quantityDisplay}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span>{formatDate(movement.createdAt)}</span>
                        <span>•</span>
                        <span>{formatUser(movement.createdBy)}</span>
                      </div>

                      {movement.reason && (
                        <p className="text-sm text-gray-600 mb-2">
                          {movement.reason}
                        </p>
                      )}

                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{t('products.stock.stockLabel')}: {movement.previousStock}</span>
                        <span>→</span>
                        <span className="font-medium text-gray-600">{movement.newStock}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-600">
              {t('products.stock.movementCount', { count: total })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600">
                {t('common.pagination.page')} {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockMovementHistory;
